import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';

import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import { PlayerStatisticsService } from 'services/api/player-statistics';

type PlayerSearchResult = {
  id: string;
  nickname: string;
  email: string;
  registered: string;
};

type GameCategoryStats = {
  freePlay: number;
  cash: number;
  tournament: number;
  league: number;
  total: number;
};

type PlayerStats = PlayerSearchResult & {
  balance: {
    play: number;
    coins: number;
    real: number;
    bonus: number;
    referralRevenueShare: number;
    rakeBack: number;
  };
  games: GameCategoryStats;
};

const dateFormatter = new Intl.DateTimeFormat('ru-RU', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Kolkata',
});

const numberFormatter = new Intl.NumberFormat('ru-RU', {
  maximumFractionDigits: 2,
});

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  title: {
    margin: 0,
  },
  searchForm: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: '100%',
    maxWidth: 560,
  },
  searchButton: {
    minHeight: 40,
  },
  resultList: {
    maxHeight: 420,
    overflowY: 'auto',
    borderRight: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      maxHeight: 260,
      borderRight: 0,
      borderBottom: `1px solid ${theme.palette.divider}`,
      marginBottom: theme.spacing(2),
    },
  },
  resultItem: {
    alignItems: 'flex-start',
  },
  selectedResult: {
    backgroundColor: 'rgba(0, 188, 212, 0.10)',
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: theme.spacing(1),
  },
  meta: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    gap: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    marginBottom: theme.spacing(2),
    '& dt': {
      fontWeight: 700,
    },
    '& dd': {
      margin: 0,
      overflowWrap: 'anywhere',
    },
  },
  tables: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(3),
    marginTop: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  headerCell: {
    fontWeight: 700,
  },
  numericCell: {
    textAlign: 'right',
    fontWeight: 600,
  },
  state: {
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  error: {
    color: theme.palette.error.main,
  },
}));

const gameRows: Array<{ key: keyof GameCategoryStats; label: string }> = [
  { key: 'freePlay', label: 'Free Play' },
  { key: 'cash', label: 'Игры на деньги' },
  { key: 'tournament', label: 'Обычные турниры' },
  { key: 'league', label: 'Турниры-лиги' },
  { key: 'total', label: 'Всего' },
];

const balanceRows: Array<{ key: keyof PlayerStats['balance']; label: string }> = [
  { key: 'play', label: 'Основной баланс' },
  { key: 'coins', label: 'Монеты Free Play' },
  { key: 'real', label: 'Реальный баланс' },
  { key: 'bonus', label: 'Бонусы' },
  { key: 'referralRevenueShare', label: 'Реферальный доход' },
  { key: 'rakeBack', label: 'Рейкбэк' },
];

export default function PlayerStatistics() {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<PlayerSearchResult[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [player, setPlayer] = useState<PlayerStats | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingPlayer, setIsLoadingPlayer] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [playerError, setPlayerError] = useState('');

  const searchPlayers = useCallback(async (value: string) => {
    const normalized = value.trim();
    if (!normalized) {
      setResults([]);
      setSearchError('');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    try {
      const response = await PlayerStatisticsService.search(normalized);
      setResults(response.data || []);
    } catch (error) {
      setResults([]);
      setSearchError('Не удалось выполнить поиск игрока');
    } finally {
      setIsSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => searchPlayers(query), 300);
    return () => window.clearTimeout(timer);
  }, [query, searchPlayers]);

  const loadPlayer = useCallback(async (id: string) => {
    setSelectedId(id);
    setIsLoadingPlayer(true);
    setPlayerError('');
    try {
      const response = await PlayerStatisticsService.getById(id);
      setPlayer(response.data as PlayerStats);
    } catch (error) {
      setPlayer(null);
      setPlayerError('Не удалось загрузить статистику игрока');
    } finally {
      setIsLoadingPlayer(false);
    }
  }, []);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    searchPlayers(query);
  };

  return (
    <GridContainer>
      {/* @ts-ignore */}
      <GridItem xs={12}>
        <Card>
          <CardHeader color="success">
            <div className={classes.header}>
              <h4 className={classes.title}>Статистика игрока</h4>
              <Tooltip title="Обновить карточку">
                <span>
                  <IconButton
                    aria-label="Обновить статистику игрока"
                    color="inherit"
                    disabled={!selectedId || isLoadingPlayer}
                    onClick={() => loadPlayer(selectedId)}
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          </CardHeader>
          <CardBody>
            <form className={classes.searchForm} onSubmit={submitSearch}>
              <TextField
                className={classes.searchField}
                autoFocus
                label="ID, никнейм или email"
                variant="outlined"
                size="small"
                value={query}
                inputProps={{ maxLength: 100 }}
                onChange={(event) => setQuery(event.target.value)}
              />
              <Tooltip title="Найти">
                <span>
                  <Button
                    className={classes.searchButton}
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={!query.trim() || isSearching}
                    aria-label="Найти игрока"
                  >
                    {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </Button>
                </span>
              </Tooltip>
            </form>

            {searchError && <Typography className={classes.error}>{searchError}</Typography>}

            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <List className={classes.resultList} aria-label="Результаты поиска игроков">
                  {!query.trim() && (
                    <ListItem>
                      <ListItemText secondary="Введите ID, никнейм или email игрока" />
                    </ListItem>
                  )}
                  {query.trim() && !isSearching && !searchError && results.length === 0 && (
                    <ListItem>
                      <ListItemText secondary="Игроки не найдены" />
                    </ListItem>
                  )}
                  {results.map((result) => (
                    <ListItem
                      button
                      className={`${classes.resultItem} ${
                        selectedId === result.id ? classes.selectedResult : ''
                      }`}
                      key={result.id}
                      selected={selectedId === result.id}
                      onClick={() => loadPlayer(result.id)}
                    >
                      <ListItemText
                        primary={result.nickname}
                        secondary={`${result.email}\n${result.id}`}
                        secondaryTypographyProps={{ style: { whiteSpace: 'pre-line' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12} md={8}>
                {isLoadingPlayer && (
                  <div className={classes.state}>
                    <CircularProgress size={36} />
                  </div>
                )}
                {!isLoadingPlayer && playerError && (
                  <div className={classes.state}>
                    <Typography className={classes.error}>{playerError}</Typography>
                  </div>
                )}
                {!isLoadingPlayer && !playerError && !player && (
                  <div className={classes.state}>Выберите игрока в результатах поиска</div>
                )}
                {!isLoadingPlayer && player && (
                  <div>
                    <h4 className={classes.sectionTitle}>{player.nickname}</h4>
                    <dl className={classes.meta}>
                      <dt>ID</dt>
                      <dd>{player.id}</dd>
                      <dt>Email</dt>
                      <dd>{player.email}</dd>
                      <dt>Регистрация</dt>
                      <dd>{dateFormatter.format(new Date(player.registered))} IST</dd>
                    </dl>
                    <Divider />

                    <div className={classes.tables}>
                      <TableContainer>
                        <Table size="small" aria-label="Игры игрока по типам">
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.headerCell}>Тип игры</TableCell>
                              <TableCell className={classes.headerCell} align="right">
                                Партий
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {gameRows.map((row) => (
                              <TableRow key={row.key}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell className={classes.numericCell}>
                                  {numberFormatter.format(player.games[row.key] || 0)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <TableContainer>
                        <Table size="small" aria-label="Балансы игрока">
                          <TableHead>
                            <TableRow>
                              <TableCell className={classes.headerCell}>Баланс</TableCell>
                              <TableCell className={classes.headerCell} align="right">
                                Значение
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {balanceRows.map((row) => (
                              <TableRow key={row.key}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell className={classes.numericCell}>
                                  {numberFormatter.format(player.balance[row.key] || 0)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  </div>
                )}
              </Grid>
            </Grid>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
